import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Order, MenuItem } from '../types';
import { apiService } from '../services/apiService';
import { formatEuro, formatDateTime } from '../utils/format';
import {
  ArrowLeft,
  History,
  Phone,
  Search,
  RefreshCw,
  ShoppingBag,
  Utensils,
  CheckCircle2,
  Clock,
  Flame,
  Award,
  Sparkles,
  ChevronDown,
  ChevronUp,
  X,
  Plus
} from 'lucide-react';

export const CustomerOrderHistoryPage: React.FC = () => {
  const {
    customerProfile,
    categories,
    addToCart,
    setIsCartDrawerOpen,
    setCurrentView,
    currentTable
  } = useApp();
  const { lang, t } = useLanguage();

  const [phoneInput, setPhoneInput] = useState(customerProfile?.phone || '');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'completed' | 'active' | 'cancelled'>('all');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [reorderSuccessId, setReorderSuccessId] = useState<string | null>(null);

  const fetchOrdersForPhone = async (phoneToQuery: string) => {
    if (!phoneToQuery.trim()) return;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const results = await apiService.getCustomerOrdersByPhone(phoneToQuery.trim());
      setOrders(results || []);
      if (results && results.length > 0) {
        setExpandedOrderId(results[0].id);
      }
    } catch (e) {
      console.error('Failed to fetch past orders:', e);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (customerProfile?.phone) {
      setPhoneInput(customerProfile.phone);
      fetchOrdersForPhone(customerProfile.phone);
    } else {
      setOrders([]);
      setHasSearched(false);
      setShowSearchBox(true);
    }
  }, [customerProfile?.phone]);

  const totalSpent = orders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
  const totalOrders = orders.length;

  const filteredOrders = orders.filter(o => {
    if (selectedFilter === 'completed') return o.status === 'completed';
    if (selectedFilter === 'active') return ['submitted', 'confirmed', 'preparing', 'ready'].includes(o.status);
    if (selectedFilter === 'cancelled') return o.status === 'cancelled';
    return true;
  });

  const toggleExpandOrder = (orderId: string) => {
    setExpandedOrderId(prev => (prev === orderId ? null : orderId));
  };

  const handleReorder = (order: Order) => {
    const allMenuItems = categories.flatMap(c => c.items || []);
    order.items.forEach(orderItem => {
      const found = allMenuItems.find(
        m =>
          m.name_fi.toLowerCase() === orderItem.name.toLowerCase() ||
          m.name_en.toLowerCase() === orderItem.name.toLowerCase() ||
          m.name_sv.toLowerCase() === orderItem.name.toLowerCase() ||
          m.id === orderItem.id
      );

      const itemToAdd: MenuItem = found || {
        id: orderItem.id || `item_${Date.now()}`,
        category_id: 'cat_pizza',
        name_fi: orderItem.name,
        name_en: orderItem.name,
        name_sv: orderItem.name,
        description_fi: '',
        description_en: '',
        description_sv: '',
        price: orderItem.price || 0,
        vat_rate: 14,
        image_url: '',
        is_available: true,
        spice_level: 0,
        dietary_flags: [],
        sort_order: 1
      };

      addToCart(
        itemToAdd,
        orderItem.quantity || 1,
        orderItem.selected_variants || [],
        orderItem.special_instructions
      );
    });

    setReorderSuccessId(order.id);
    setTimeout(() => {
      setReorderSuccessId(null);
      setIsCartDrawerOpen(true);
    }, 600);
  };

  const getStatusPill = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(16, 185, 129, 0.12)',
              color: 'var(--accent-emerald)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px'
            }}
          >
            <CheckCircle2 size={10} />
            <span>{lang === 'en' ? 'Completed' : 'Valmis'}</span>
          </span>
        );
      case 'ready':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(56, 189, 248, 0.12)',
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.25)'
            }}
          >
            {lang === 'en' ? 'Ready' : 'Tarjoiltu'}
          </span>
        );
      case 'preparing':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(224, 90, 54, 0.12)',
              color: 'var(--accent-copper)',
              border: '1px solid rgba(224, 90, 54, 0.25)'
            }}
          >
            {lang === 'en' ? 'In Oven' : 'Uunissa'}
          </span>
        );
      case 'confirmed':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(245, 158, 11, 0.12)',
              color: 'var(--accent-amber)',
              border: '1px solid rgba(245, 158, 11, 0.25)'
            }}
          >
            {lang === 'en' ? 'Confirmed' : 'Vahvistettu'}
          </span>
        );
      case 'cancelled':
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--accent-rose)',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}
          >
            {lang === 'en' ? 'Cancelled' : 'Peruttu'}
          </span>
        );
      default:
        return (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(245, 158, 11, 0.12)',
              color: 'var(--accent-amber)',
              border: '1px solid rgba(245, 158, 11, 0.25)'
            }}
          >
            {lang === 'en' ? 'Received' : 'Vastaanotettu'}
          </span>
        );
    }
  };

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '10px 14px 70px' }}>
      {/* 1. Compact Slim Navigation & Header Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px 12px',
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-medium)',
          boxShadow: 'var(--shadow-sm)',
          marginBottom: '10px'
        }}
      >
        <button
          onClick={() => setCurrentView('customer')}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)',
            padding: '6px 12px',
            borderRadius: 'var(--radius-full)',
            fontWeight: 700,
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={14} color="var(--accent-copper)" />
          <span>{lang === 'en' ? 'Menu' : 'Menu'}</span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <History size={16} color="var(--accent-copper)" />
          <span style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)', fontFamily: 'var(--font-heading)' }}>
            {lang === 'en' ? 'Order History' : 'Tilaushistoria'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={() => setShowSearchBox(!showSearchBox)}
            style={{
              background: showSearchBox ? 'var(--accent-copper)' : 'var(--bg-surface)',
              color: showSearchBox ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border-subtle)',
              padding: '6px 10px',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.75rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer'
            }}
            title="Search by phone"
          >
            <Phone size={12} />
            <span className="desktop-only">{phoneInput ? phoneInput.slice(-4) : 'Search'}</span>
          </button>

          <span
            className="table-pill"
            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
          >
            {t('table')} {currentTable?.table_number || '1'}
          </span>
        </div>
      </div>

      {/* 2. Optional Quick Search Collapsible Bar */}
      {showSearchBox && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            marginBottom: '10px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <form
            onSubmit={e => {
              e.preventDefault();
              fetchOrdersForPhone(phoneInput);
              setShowSearchBox(false);
            }}
            style={{ display: 'flex', gap: '6px' }}
          >
            <div style={{ position: 'relative', flex: 1 }}>
              <Phone
                size={14}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }}
              />
              <input
                type="tel"
                className="form-input"
                placeholder="+358 40 123 4567"
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                style={{
                  paddingLeft: '32px',
                  height: '36px',
                  fontSize: '0.85rem',
                  borderRadius: 'var(--radius-sm)'
                }}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading || !phoneInput.trim()}
              style={{
                height: '36px',
                padding: '0 14px',
                fontSize: '0.8rem',
                gap: '5px',
                borderRadius: 'var(--radius-sm)'
              }}
            >
              {isLoading ? <RefreshCw size={13} className="anim-spin" /> : <Search size={13} />}
              <span>{lang === 'en' ? 'Search' : 'Hae'}</span>
            </button>
          </form>
        </div>
      )}

      {/* 3. Ultra-Compact Customer Profile Ribbon (1-line sleek chip) */}
      {hasSearched && totalOrders > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '10px',
            fontSize: '0.8rem'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <div
              style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: 'var(--accent-copper)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: '0.68rem',
                flexShrink: 0
              }}
            >
              {(orders[0]?.customer_name || 'A')[0].toUpperCase()}
            </div>
            <span style={{ fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {orders[0]?.customer_name || 'Asiakas'}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>•</span>
            <span style={{ color: 'var(--accent-copper)', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {totalOrders} {lang === 'en' ? 'orders' : 'tilausta'}
            </span>
          </div>

          <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '0.82rem', flexShrink: 0 }}>
            {formatEuro(totalSpent)}
          </div>
        </div>
      )}

      {/* 4. Slim Filter Tabs */}
      {hasSearched && orders.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', overflowX: 'auto', paddingBottom: '2px' }}>
          {[
            { id: 'all', labelEn: `All (${orders.length})`, labelFi: `Kaikki (${orders.length})` },
            { id: 'completed', labelEn: 'Completed', labelFi: 'Valmiit' },
            { id: 'active', labelEn: 'Active', labelFi: 'Käynnissä' },
            { id: 'cancelled', labelEn: 'Cancelled', labelFi: 'Perutut' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedFilter(tab.id as any)}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.74rem',
                fontWeight: 700,
                border: '1px solid',
                borderColor: selectedFilter === tab.id ? 'var(--accent-copper)' : 'var(--border-subtle)',
                background: selectedFilter === tab.id ? 'var(--accent-copper)' : 'var(--bg-card)',
                color: selectedFilter === tab.id ? '#ffffff' : 'var(--text-secondary)',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {lang === 'en' ? tab.labelEn : tab.labelFi}
            </button>
          ))}
        </div>
      )}

      {/* 5. Minimal Accordion Order Cards List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isLoading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-muted)'
            }}
          >
            <RefreshCw size={24} className="anim-spin" style={{ margin: '0 auto 8px', color: 'var(--accent-copper)' }} />
            <div style={{ fontSize: '0.85rem' }}>
              {lang === 'en' ? 'Loading orders...' : 'Haetaan tilauksia...'}
            </div>
          </div>
        ) : !hasSearched ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-muted)'
            }}
          >
            <ShoppingBag size={36} style={{ margin: '0 auto 8px', opacity: 0.35 }} />
            <p style={{ fontSize: '0.85rem', margin: 0 }}>
              {lang === 'en'
                ? 'Enter your mobile number to see past orders.'
                : 'Syötä puhelinnumerosi nähdäksesi aiemmat tilauksesi.'}
            </p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-muted)'
            }}
          >
            <Utensils size={36} style={{ margin: '0 auto 8px', opacity: 0.35 }} />
            <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>
              {lang === 'en' ? 'No orders found' : 'Ei tilauksia'}
            </p>
          </div>
        ) : (
          filteredOrders.map(order => {
            const isExpanded = expandedOrderId === order.id;
            const previewText = (order.items || [])
              .map(it => `${it.quantity}x ${it.name}`)
              .join(', ');

            return (
              <div
                key={order.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                {/* Clickable Compact Card Header */}
                <div
                  onClick={() => toggleExpandOrder(order.id)}
                  style={{
                    padding: '10px 12px',
                    background: 'var(--bg-surface)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        fontSize: '0.84rem',
                        fontWeight: 800,
                        color: 'var(--accent-copper)',
                        background: 'rgba(224, 90, 54, 0.1)',
                        padding: '1px 6px',
                        borderRadius: 'var(--radius-xs)'
                      }}
                    >
                      {order.order_number}
                    </span>

                    <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                      Pöytä #{order.table_number}
                    </span>

                    {getStatusPill(order.status)}

                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Clock size={10} />
                      <span>{formatDateTime(order.created_at)}</span>
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                      {formatEuro(order.total_amount)}
                    </span>
                    <div style={{ color: 'var(--text-muted)' }}>
                      {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </div>
                  </div>
                </div>

                {/* Collapsed One-line Dish Summary */}
                {!isExpanded && (
                  <div
                    onClick={() => toggleExpandOrder(order.id)}
                    style={{
                      padding: '0 12px 8px',
                      background: 'var(--bg-surface)',
                      fontSize: '0.74rem',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {previewText}
                  </div>
                )}

                {/* Expanded Dish Breakdown & 1-Click Reorder */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '10px 12px',
                      borderTop: '1px solid var(--border-subtle)',
                      background: 'var(--bg-primary)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        paddingRight: '4px'
                      }}
                    >
                      {(order.items || []).map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            padding: '6px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border-subtle)',
                            fontSize: '0.8rem'
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1, paddingRight: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ fontWeight: 800, color: 'var(--accent-copper)', minWidth: '20px' }}>
                                {item.quantity}x
                              </span>
                              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                                {item.name}
                              </span>
                            </div>
                            {item.selected_variants && item.selected_variants.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '25px', marginTop: '1px' }}>
                                {item.selected_variants.map((v: any) => v.option_name || v.name).join(', ')}
                              </div>
                            )}
                            {item.special_instructions && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--accent-amber)', fontStyle: 'italic', marginLeft: '25px', marginTop: '1px' }}>
                                "{item.special_instructions}"
                              </div>
                            )}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            {formatEuro((item.price || 0) * (item.quantity || 1))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {order.special_notes && (
                      <div
                        style={{
                          padding: '6px 10px',
                          borderRadius: 'var(--radius-xs)',
                          background: 'rgba(245, 158, 11, 0.08)',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          fontSize: '0.72rem',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        <strong>{lang === 'en' ? 'Notes:' : 'Huomio:'}</strong> "{order.special_notes}"
                      </div>
                    )}

                    {/* Order Action Bar */}
                    <div
                      style={{
                        borderTop: '1px dashed var(--border-subtle)',
                        paddingTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {order.vat_food_amount ? `Ruoka ALV 14%: ${formatEuro(order.vat_food_amount)}` : ''}
                        {order.vat_beverage_amount ? ` • Juomat ALV 25.5%: ${formatEuro(order.vat_beverage_amount)}` : ''}
                        {!order.vat_food_amount && !order.vat_beverage_amount && `${order.items?.length || 0} ${lang === 'en' ? 'items' : 'tuotetta'}`}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleReorder(order)}
                        style={{
                          background: reorderSuccessId === order.id ? 'var(--accent-emerald)' : 'var(--accent-copper)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        <Sparkles size={13} />
                        <span>
                          {reorderSuccessId === order.id
                            ? (lang === 'en' ? 'Added!' : 'Lisätty!')
                            : (lang === 'en' ? 'Reorder' : 'Tilaa uudelleen')}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Floating Bottom Menu Bar */}
      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 90
        }}
      >
        <button
          onClick={() => setCurrentView('customer')}
          className="btn-primary"
          style={{
            padding: '10px 20px',
            fontSize: '0.86rem',
            boxShadow: '0 6px 20px rgba(224, 90, 54, 0.35)',
            borderRadius: 'var(--radius-full)',
            gap: '6px',
            fontWeight: 700
          }}
        >
          <Utensils size={15} />
          <span>{lang === 'en' ? 'Explore Full Menu' : 'Selaa Menua'}</span>
        </button>
      </div>
    </div>
  );
};
